import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Lock, Loader2 } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import LanguagePicker from "@/components/LanguagePicker";
import { IconField, FormAlert } from "@/components/auth/IconField";
import { LoginHero, LoginFooterLinks } from "@/components/auth/LoginHero";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await base44.auth.loginViaEmailPassword(email, password);
      window.location.href = "/";
    } catch {
      setError(t("login.errorInvalid"));
    } finally {
      setLoading(false);
    }
  };

  return <div className="min-h-screen bg-background flex flex-col">
    <div className="h-1 w-full bg-gradient-to-r from-primary/20 via-primary to-primary/20" />
    <div className="absolute top-3 right-3 z-20"><LanguagePicker /></div>
    <div className="flex flex-1 flex-col md:flex-row">
      <LoginHero />
      <div className="w-full md:flex-1 flex flex-col items-center justify-center px-6 py-10 md:py-12 md:px-12">
        <div className="w-full max-w-sm space-y-7">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold text-foreground">{t("login.title")}</h2>
            <p className="text-muted-foreground text-sm">{t("login.subtitle")}</p>
          </div>
          <FormAlert message={error} testId="login-error" />
          <form onSubmit={handleSubmit} className="space-y-4">
            <IconField id="email" icon={Mail} label={t("common.email")} data-testid="login-email-input"
              type="email" autoComplete="email" autoFocus placeholder="seu@email.com" required
              value={email} onChange={(event) => setEmail(event.target.value)} />
            <IconField id="password" icon={Lock} label={t("common.password")} data-testid="login-password-input"
              type="password" autoComplete="current-password" placeholder="••••••••" required
              value={password} onChange={(event) => setPassword(event.target.value)}>
              <Link to="/forgot-password" data-testid="login-forgot-password" className="text-xs text-primary hover:underline">
                {t("login.forgotPassword")}
              </Link>
            </IconField>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base mt-2">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {loading ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>
          <LoginFooterLinks />
        </div>
      </div>
    </div>
  </div>;
}
